'use client';
import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { I18N_LANGUAGES } from '@/i18n/config';
import {
  BetweenHorizontalStart,
  Building2,
  Coffee,
  CreditCard,
  FileText,
  Globe,
  Moon,
  Newspaper,
  Settings,
  Shield,
  User,
  UserCircle,
  Users,
} from 'lucide-react';
import { BackendAuth } from '@/lib/auth-backend';
import { useTheme } from 'next-themes';
import { useEnhancedLanguage } from '@/providers/enhanced-i18n-provider';
import { useTranslation } from '@/hooks/useTranslation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { AdminProfileSections } from '@/app/components/layouts/demo1/components/admin-profile-sections';

export function UserDropdownMenu({ trigger }) {
  const user = BackendAuth.getUser();
  const { changeLanguage, language } = useEnhancedLanguage();
  const { t } = useTranslation();
  const { resolvedTheme, setTheme } = useTheme();
  const [developerPlan, setDeveloperPlan] = useState(null);

  // /admin/company is org-admin-only: visible only when the user's rights
  // groups (kontakt_rights_group) include group 1 = Admin. is_admin is the
  // backend-computed equivalent of exactly that check (user.py:_compute_is_admin),
  // kept as fallback for stored sessions missing the raw rights string.
  const isAdmin = useMemo(() => {
    if (!user) return false;
    const rightsRaw = user.kontakt_rights_group;
    if (typeof rightsRaw === 'string' && rightsRaw.trim() !== '') {
      return rightsRaw.split(',').some((g) => g.trim() === '1');
    }
    return user.is_admin === true || user.is_admin === 1;
  }, [user]);

  // Fetch developer plan on mount
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await BackendAuth.apiCallJson('/api/accounting/subscriptions/my-developer-plan');
        setDeveloperPlan(response.plan_short);
      } catch (error) {
        console.error('Failed to fetch developer plan:', error);
      }
    };
    if (BackendAuth.isAuthenticated()) {
      fetchPlan();
    }
  }, []);

  // Ensure we have a complete language object with flag
  const currentLanguage = language || I18N_LANGUAGES.find(lang => lang.code === 'de') || I18N_LANGUAGES[0];

  const handleLanguage = (lang) => {
    changeLanguage(lang.code);
  };

  const handleThemeToggle = (checked) => {
    setTheme(checked ? 'dark' : 'light');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-72" side="bottom" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <img
              className="w-9 h-9 rounded-full border border-border"
              src={BackendAuth.getUserAvatarUrl()}
              alt={t('user_menu.avatarAlt', { editable: false })}
            />

            <div className="flex flex-col">
              <Link
                href="/account/home/get-started"
                className="text-sm text-mono hover:text-primary font-semibold"
              >
                {user?.name || ''}
              </Link>
              <span className="text-xs text-muted-foreground truncate">
                {user?.email || ''}
              </span>
            </div>
          </div>
{developerPlan && (
            <Badge
              variant={developerPlan === 'BASIC' ? 'secondary' : 'primary'}
              appearance="light"
              size="sm"
            >
              {developerPlan}
            </Badge>
          )}
        </div>

        <DropdownMenuSeparator />

        {/* Menu Items */}
        <DropdownMenuItem asChild>
          <Link
            href="/profile"
            className="flex items-center gap-2"
          >
            <User />
            {t('user_menu.profile')}
          </Link>
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link
              href="/admin/company"
              className="flex items-center gap-2"
            >
              <Building2 />
              {t('user_menu.company')}
            </Link>
          </DropdownMenuItem>
        )}

        {/* KI-Modell-News: für ALLE eingeloggten User sichtbar (kein Admin-Gate) */}
        <DropdownMenuItem asChild>
          <Link
            href="/ai/model-news"
            className="flex items-center gap-2"
          >
            <Newspaper />
            {t('user_menu.model_news', { defaultValue: 'KI-Modell-News', editable: false })}
          </Link>
        </DropdownMenuItem>

        {/* Language Submenu with Radio Group */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="flex items-center gap-2 [&_[data-slot=dropdown-menu-sub-trigger-indicator]]:hidden hover:[&_[data-slot=badge]]:border-input data-[state=open]:[&_[data-slot=badge]]:border-input">
            <Globe />
            <span className="flex items-center justify-between gap-2 grow relative">
              {t('user_menu.language')}
              <Badge
                variant="outline"
                className="absolute end-0 top-1/2 -translate-y-1/2 flex items-center gap-1.5"
              >
                {currentLanguage?.flag && (
                  <img
                    src={currentLanguage.flag}
                    className="w-3.5 h-3.5 rounded-full"
                    alt={currentLanguage?.name || 'Language'}
                  />
                )}
                {currentLanguage?.name || currentLanguage?.shortName || 'Deutsch'}
              </Badge>
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-48 max-h-[var(--radix-dropdown-menu-content-available-height,80vh)] overflow-y-auto">
            <DropdownMenuRadioGroup
              value={currentLanguage?.code || 'de'}
              onValueChange={(value) => {
                const selectedLang = I18N_LANGUAGES.find(
                  (lang) => lang.code === value,
                );
                if (selectedLang) handleLanguage(selectedLang);
              }}
            >
              {I18N_LANGUAGES.map((item) => (
                <DropdownMenuRadioItem
                  key={item.code}
                  value={item.code}
                  className="flex items-center gap-2"
                >
                  <img
                    src={item.flag}
                    className="w-4 h-4 rounded-full"
                    alt={item.name}
                  />

                  <span>{item.name}</span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* Admin + Super-Admin sections (moved here from the standalone Shield
            menu). Renders nothing for non-admins; renders its own border-t
            separators per section. */}
        <AdminProfileSections />

        <DropdownMenuSeparator />

        {/* Footer */}
        <DropdownMenuItem
          className="flex items-center gap-2"
          onSelect={(event) => event.preventDefault()}
        >
          <Moon />
          <div className="flex items-center gap-2 justify-between grow">
            {t('user_menu.darkMode')}
            <Switch
              size="sm"
              checked={resolvedTheme === 'dark'}
              onCheckedChange={handleThemeToggle}
            />
          </div>
        </DropdownMenuItem>
        <div className="p-2 mt-1">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              BackendAuth.logout();
              BackendAuth.redirectToLogin(window.location.href);
            }}
          >
            {t('user_menu.logout', { editable: false })}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
